import "./Unlock.css";
import { useLocation, useNavigate } from "react-router-dom";
import { Content, Header, Main } from "../../commons/Template/Template";
import { Trans, useTranslation } from "react-i18next";
import Pill from "../Common/Pill/Pill";
import ProgressBar from "../Common/ProgressBar/ProgressBar";
import { useEffect, useState } from "react";
import Button from "../../commons/Button/Button";
import { CheckCircleRounded, Grading, FileCopy } from "@mui/icons-material";
import routes from "../../../routes/routes";
import api from "../../../api/resources";
import { Form, FormLoader } from "../../commons/Form/Form";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import { useAuth } from "../../../auth/AuthContext";
import { capitalize } from "../../../utils";
import { useHelperDispatch } from "../../commons/Helper/HelperContext";
import { PartnersDrawer } from "../Assessment/Results/Results";
import Typo from "../../commons/Typo/Typo";

export default function Unlock() {
  // Flow
  const location = useLocation();
  let need = "";
  let results = null;
  if (location.state) {
    need = location.state.need;
    results = location.state.results;
  }
  const navigate = useNavigate();
  const goBack = () => {
    navigate(routes.Financing.assess);
  };
  useEffect(() => {
    if (!need || !results) {
      goBack();
    }
  }, []);
  const goForward = (usecase) => {
    const target = `${routes.Financing.request}/new`;
    const state = {
      need: need,
      usecase: usecase,
    };
    navigate(target, { state: state });
  };

  const { t } = useTranslation(["common", "financing"]);
  document.title = t("financing:unlock.title");

  const [loading, setLoading] = useState(true);
  const { loadingActiveCompany } = useAuth();

  const [selectedUsecase, setSelectedUsecase] = useState("");

  const [usecases, setUsecases] = useState(null);
  const dispatchModal = useModalDispatch();
  const dispatchHelper = useHelperDispatch();

  // Delete potential previous helper
  useEffect(() => {
    dispatchHelper({ type: "delete" });
  }, []);

  const initUsecases = (
    jsonUsecases = {
      usecaseA: {
        status: "unavailable",
        reasons: ["activity"],
        actions: {},
      },
      usecaseB: {
        status: "available",
        reasons: [],
        actions: {
          add_representant_email: true,
          synchronise_accounts: false,
        },
      },
      usecaseD: {
        status: "active",
        reasons: ["activity"],
        actions: {
          add_representant_email: true,
          synchronise_accounts: true,
        },
      },
    },
  ) => {
    const newUsecases = Object.entries(jsonUsecases).map(
      ([usecaseID, usecaseContent]) => {
        const { status, reasons } = usecaseContent;

        let actions = [];

        // ----- Actions -----
        const hasActions =
          usecaseContent.actions &&
          Object.keys(usecaseContent.actions).length > 0;
        if (hasActions) {
          actions = Object.entries(usecaseContent.actions).map(
            ([actionID, actionDone]) => ({ id: actionID, done: actionDone }),
          );
        }

        const initActionButton = (actionID) => {
          const onClickFn = {
            IMPORT_FINANCIAL_STATEMENT: () => navigate(routes.Vault.finance),
            IMPORT_BANKING_DATA: () => navigate(routes.Vault.bank),
          };

          const handleActionWithHelper = (actionID) => {
            const actionHelper = {
              heading: t("financing:unlock.actions.helper.heading"),
              subheading: t("financing:unlock.actions.helper.subheading"),
              button: t("financing:unlock.actions.helper.button"),
              navigate: {
                target: routes.Financing.unlock,
                state: {
                  need: need,
                },
              },
            };

            onClickFn[actionID]();
            dispatchHelper({
              type: "add",
              expand: true,
              ...actionHelper,
            });
          };

          return {
            onClick: () => handleActionWithHelper(actionID),
            text: t(`financing:unlock.actions.ids.${actionID}.button`),
          };
        };

        const initActions = (
          actions = [
            { id: "actionID1", done: true },
            { id: "actionID2", done: false },
          ],
        ) =>
          actions.map((action) => ({
            id: action.id,
            icon: <FileCopy />,
            title: t(`financing:unlock.actions.ids.${action.id}.title`),
            subtitle: t(`financing:unlock.actions.ids.${action.id}.subtitle`),
            button: action.done ? null : initActionButton(action.id),
            buttonAlt: action.done,
          }));

        // ---- Use cases ---- //

        const initUsecaseElements = () => {
          const pill = (color, text) => ({
            color: color,
            text: text,
          });

          const moreInfoButton = () => {
            const moreInfoModalNote = reasons && (
              <ul style={{ padding: "revert", margin: "0" }}>
                {reasons.map((reasonID, index) => (
                  <li style={{ listStyle: "revert" }} key={index}>
                    {capitalize(
                      t(
                        `financing:unlock.usecases.modals.moreInfo.note.reasons.${reasonID}`,
                      ),
                    )}
                  </li>
                ))}
              </ul>
            );

            const moreInfoModal = {
              title: t("financing:unlock.usecases.modals.moreInfo.title"),
              message: t("financing:unlock.usecases.modals.moreInfo.message"),
              note: moreInfoModalNote,
              size: "medium",
            };

            return {
              text: t("financing:unlock:usecases.buttons.moreInfo"),
              onClick: () =>
                dispatchModal({
                  type: "add",
                  ...moreInfoModal,
                }),
            };
          };

          const viewActionsButton = () => ({
            text: t("financing:unlock.usecases.buttons.unlock"),
            onClick: () => setSelectedUsecase(usecaseID),
          });

          const requestFinancingButton = () => {
            let button = {
              text: t("financing:unlock.usecases.buttons.request"),
            };

            const initRedirectModal = (provider, redirectURL) => {
              return {
                title: t("financing:unlock.usecases.modals.redirect.title"),
                message: (
                  <Trans
                    t={t}
                    i18nKey={
                      "financing:unlock.usecases.modals.redirect.message"
                    }
                    values={{ provider }}
                  />
                ),
                component: (
                  <Form
                    button={t(
                      "financing:unlock.usecases.modals.redirect.button",
                    )}
                    onSubmit={() => (window.location.href = redirectURL)}
                  />
                ),
              };
            };

            const handleGCollect = () => {
              const redirectURL =
                "https://pro.gcollect.fr/inscription/c/trouver-votre-entreprise?affiliation_code=LEA-0-100";
              const note1URL = "https://www.gcollect.fr/9155-2/";

              const note = (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <Typo>
                    <Trans
                      t={t}
                      i18nKey={
                        "financing:unlock.usecases.modals.debt_collection.GCollectNotes.note1"
                      }
                      components={{ anchor: <a href={note1URL} /> }}
                    />
                  </Typo>
                  <Typo>
                    {t(
                      "financing:unlock.usecases.modals.debt_collection.GCollectNotes.note2",
                    )}
                  </Typo>
                </div>
              );

              const modal = {
                ...initRedirectModal("GCollect", redirectURL),
                note,
              };

              dispatchModal({ type: "add", ...modal });
            };

            const handleDefacto = () => {
              const redirectURL =
                "https://app.getdefacto.com/onboarding?referral=DEFACTO-LEANO-REFERRAL";

              const modal = {
                ...initRedirectModal("Defacto", redirectURL),
              };

              dispatchModal({ type: "add", ...modal });
            };

            switch (usecaseID) {
              case "debt_collection":
                button = {
                  ...button,
                  text: t("financing:unlock.usecases.buttons.collect"),
                  onClick: handleGCollect,
                };
                break;
              case "short-term_invoice":
                button = {
                  ...button,
                  text: t("financing:unlock.usecases.buttons.request"),
                  onClick: handleDefacto,
                };
                break;

              default:
                button = {
                  ...button,
                  onClick: () => goForward(usecaseID),
                };
            }

            return button;
          };

          const variants = {
            unavailable: {
              pill: pill("red", capitalize(t("common:unavailable"))),
              button: moreInfoButton(),
            },
            available: {
              pill: pill("yellow", capitalize(t("common:toUnlock"))),
              button: viewActionsButton(),
            },
            active: {
              pill: pill("green", capitalize(t("common:available"))),
              button: requestFinancingButton(),
            },
          };

          return variants[status];
        };

        return {
          id: usecaseID,
          icon: <Grading />,
          title: t(`financing:unlock.usecases.ids.${usecaseID}.title`),
          subtitle: t(`financing:unlock.usecases.ids.${usecaseID}.subtitle`),
          ...initUsecaseElements(),
          actions: initActions(actions),
        };
      },
    );

    setUsecases(newUsecases);
  };

  const init = async () => {
    const response = await api.getUsecases(need);
    if (response.ok) {
      const jsonUsecases = await response.json();
      const testUsecases = false;
      testUsecases ? initUsecases() : initUsecases(jsonUsecases);
    }
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  if (!selectedUsecase)
    return (
      <Usecases
        need={need}
        usecases={usecases}
        onBack={goBack}
        loading={loading || loadingActiveCompany}
        results={results}
      />
    );

  const useCase = usecases.filter(
    (usecase) => usecase.id === selectedUsecase,
  )[0];
  return (
    <Actions
      id={useCase.id}
      actions={useCase.actions}
      onBack={() => setSelectedUsecase("")}
      loading={loading || loadingActiveCompany}
    />
  );
}

function Usecases({ need, usecases, onBack, loading, results }) {
  const { t } = useTranslation(["common", "financing"]);

  const [drawer, setDrawer] = useState(false);
  const toggleDrawer = () => setDrawer((drawer) => !drawer);

  return (
    usecases && (
      <Main themeSwitch>
        <Header onBackButtonClick={onBack}>
          <Typo variant="h1">
            {t(`financing:assessment.results.keys.${need}.name`)}
          </Typo>
          <Typo variant="body2">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <span>{t("financing:unlock.usecases.subheading")}</span>
              <a
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={toggleDrawer}
              >
                {t("financing:unlock.usecases.subheading_link")}
              </a>
            </div>
          </Typo>
        </Header>
        <Content>
          {loading ? <FormLoader /> : <Cards cards={usecases} />}
          <PartnersDrawer
            result={need && Object.entries(results[need])}
            open={drawer}
            onClose={toggleDrawer}
          />
        </Content>
      </Main>
    )
  );
}

function Actions({ id, actions, onBack, loading }) {
  const { t } = useTranslation(["common", "financing"]);

  return (
    actions && (
      <Main themeSwitch>
        <Header onBackButtonClick={onBack}>
          <Typo variant="h1">
            {t(`financing:unlock.usecases.ids.${id}.title`)}
          </Typo>
          <Typo variant="body2">
            {t("financing:unlock.actions.subheading")}
          </Typo>
        </Header>
        <Content>
          {loading ? (
            <FormLoader />
          ) : (
            <div className="Actions">
              <div className="Actions-header">
                <div className="Actions-header-text">
                  <div className="Actions-header-text-heading">
                    {t("financing:unlock.actions.counter.heading")}
                  </div>
                  <div className="Actions-header-text-subheading">
                    {t("financing:unlock.actions.counter.subheading")}
                  </div>
                </div>
                <div className="Actions-header-progress">
                  <ProgressBar
                    status
                    length={actions.length}
                    index={actions.filter((action) => !action.button).length}
                  />
                </div>
              </div>
              <Cards cards={actions} />
            </div>
          )}
        </Content>
      </Main>
    )
  );
}

function Cards({ cards }) {
  return (
    <div className="Cards">
      {cards.map((card) => (
        <Card key={card.id} {...card} />
      ))}
    </div>
  );
}

function Card({
  icon,
  title,
  pill,
  subtitle,
  button,
  buttonAlt = false,
  component,
}) {
  return (
    <div className="Card">
      <div className="Card-icon">{icon}</div>

      <div className="Card-container">
        <div className="Card-container-description">
          <div className="Card-container-description-title">
            <div className="Card-container-description-title-text">{title}</div>
            <div className="Card-container-description-title-pill">
              {pill && <Pill color={pill.color} text={pill.text} />}
            </div>
          </div>
          <Typo className="Card-container-description-subtitle">
            {subtitle}
          </Typo>
          {component && (
            <div className="Card-container-description-component">
              {component}
            </div>
          )}
        </div>
        <div className="Card-container-cta">
          {button ? (
            <Button
              className="Card-container-cta-button"
              variant={"primary_a"}
              text={button.text}
              onClick={button.onClick}
              icon={button.icon}
              iconLeft={button.iconLeft}
              disabled={button.disabled}
            />
          ) : buttonAlt ? (
            <CheckCircleRounded className="Card-container-cta-check" />
          ) : null}
        </div>
      </div>
    </div>
  );
}
